import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { MusicVideoSongMap } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../common/types';
import { stateMessages } from '../messages/stateMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DirectorProgressProps {
  title: string;
  songMap: MusicVideoSongMap;
  /** The ready own clips of this song that the director can use. */
  ownClipCount: number;
}

/**
 * The first open (design e1): the AI director makes the first cut. The song is read (the song map is
 * here), so that step is done; the director gives no progress of its own, so the next step turns
 * until the cut is written (`musicVideo.cut.updated`).
 */
const DirectorProgressComponent: React.FC<DirectorProgressProps> = ({ title, songMap, ownClipCount }) => {
  const intl = useIntl();
  const steps = [
    {
      key: 'read',
      label: intl.formatMessage(stateMessages.stepRead),
      facts: intl.formatMessage(stateMessages.stepReadFacts, {
        sections: songMap.sections.length,
        bpm: Math.round(songMap.bpm),
      }),
      state: 'done',
    },
    { key: 'pick', label: intl.formatMessage(stateMessages.stepPick), facts: '', state: 'now' },
    { key: 'cut', label: intl.formatMessage(stateMessages.stepCut), facts: '', state: 'next' },
    { key: 'text', label: intl.formatMessage(stateMessages.stepText), facts: '', state: 'next' },
  ] as const;
  return (
    <div className={styles.director} role="status">
      <h3>{intl.formatMessage(stateMessages.directorTitle, { title })}</h3>
      <p>{intl.formatMessage(stateMessages.directorText)}</p>
      <div className={styles.column}>
        {steps.map((step) => (
          <div
            key={step.key}
            className={clsx(
              styles.step,
              step.state === 'done' && styles.stepDone,
              step.state === 'now' && styles.stepNow
            )}
          >
            <span className={styles.stepDot} />
            {step.label}
            <small>{step.facts}</small>
          </div>
        ))}
      </div>
      <p className={styles.hint}>
        {ownClipCount > 0
          ? intl.formatMessage(stateMessages.directorUsesOwn, { count: ownClipCount })
          : intl.formatMessage(stateMessages.directorUsesBank)}
      </p>
    </div>
  );
};

export const DirectorProgress = TypedMemo(DirectorProgressComponent);
